/**
 * Cover (fabric/finish) swatch resolution for master products — shared by PLP tiles,
 * Contentful Card, and Shop the look.
 *
 * `getCoverSwatches`: `c_allVariantsData[].pimlyProductImageUrl` only (variant `pimlyProductImage`).
 * Covers without a URL are omitted. Order: DE-780 (`c_popularCovers`, then price / colour).
 *
 * `getPrimaryCoverSwatchDisplay`: catalog `imageGroups` via `resolveSwatchImage`.
 */

import {filterImageGroups} from '@salesforce/retail-react-app/app/utils/product-utils';

import {
  getPopularCoverIds,
  orderCoverAttributeValues,
  sortCoverVariantsForDisplay,
} from '@amblique/king-living/app/utils/variant-cover-sort';

export const COVER_SWATCH_ATTRIBUTE_ID = 'cover';

const SWATCH_VIEWTYPE_FALLBACK = ['swatch', 'large', 'hi-res'];

/**
 * Cover-scoped image only (group carries a matching `variationAttributes` entry).
 *
 * @param {object} product
 * @param {{[attributeId: string]: string}} variationValues
 * @returns {object|undefined}
 */
function resolveCoverScopedSwatchImage(product, variationValues) {
  const groups = product?.imageGroups;

  if (!Array.isArray(groups)) return undefined;

  for (const viewType of SWATCH_VIEWTYPE_FALLBACK) {
    const group = filterImageGroups(groups, {viewType, variationValues})?.[0];

    if (group?.images?.length > 0) {
      return group.images[0];
    }
  }

  return undefined;
}

/**
 * Master (un-scoped) image — same asset for every cover, so only ever a last resort.
 *
 * @param {object} product
 * @returns {object|undefined}
 */
function resolveMasterSwatchImage(product) {
  const groups = product?.imageGroups;

  if (!Array.isArray(groups)) return undefined;

  for (const viewType of SWATCH_VIEWTYPE_FALLBACK) {
    const group = groups.find(
      (g) => g?.viewType === viewType && !g.variationAttributes && g.images?.length > 0
    );

    if (group) return group.images[0];
  }

  return undefined;
}

/**
 * @param {object} product
 * @param {{[attributeId: string]: string}} variationValues
 * @returns {object|undefined} matched image (`{disBaseLink, link, alt, …}`) or undefined.
 */
export function resolveSwatchImage(product, variationValues) {
  return (
    resolveCoverScopedSwatchImage(product, variationValues) ?? resolveMasterSwatchImage(product)
  );
}

/**
 * Index `c_allVariantsData` by cover value. Parsed once per product — `findVariantEntry` would
 * re-parse the (large) JSON string for every cover, which is O(covers × payload) on 400+ cover
 * masters.
 *
 * @param {object|null|undefined} product
 * @returns {Map<string, object>|null}
 */
function buildCoverVariantIndex(product) {
  const raw = product?.c_allVariantsData;

  if (!raw) return null;

  let list;

  try {
    list = typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    return null;
  }

  if (!Array.isArray(list) || list.length === 0) return null;

  const byCover = new Map();

  for (const entry of list) {
    const cover = entry?.variationValues?.[COVER_SWATCH_ATTRIBUTE_ID];

    if (cover == null || cover === '') continue;

    const key = String(cover);
    const existing = byCover.get(key);

    // First entry wins; an orderable variant replaces a non-orderable one (multiple variants can
    // share a cover, e.g. different layouts of the same fabric).
    if (!existing) {
      byCover.set(key, entry);
    } else if (existing.orderable === false && entry.orderable !== false) {
      byCover.set(key, entry);
    }
  }

  return byCover.size > 0 ? byCover : null;
}

/**
 * Displayable cover swatches, ordered for DE-780:
 * `c_popularCovers` first (attribute order), then price ↑ / colour light→dark.
 *
 * Per-cover source: `c_allVariantsData[].pimlyProductImageUrl` only (variant-level PIM square).
 * Covers without that URL are **omitted** — callers derive "+ more" from this filtered list.
 *
 * Pass `limit` (e.g. the tile desktop cap) to stop once `limit + 1` displayable covers are found.
 * Omit `limit` to resolve every displayable cover.
 *
 * @param {object} product
 * @param {{limit?: number}} [options]
 * @returns {Array<{name?: string, value?: string, swatchUrl: string, orderable?: boolean}>}
 */
export function getCoverSwatches(product, {limit} = {}) {
  const variationAttributes = product?.variationAttributes;

  if (!Array.isArray(variationAttributes)) return [];

  const coverAttr = variationAttributes.find(({id}) => id === COVER_SWATCH_ATTRIBUTE_ID);

  if (!coverAttr?.values) return [];

  // Prefer variant-derived order (popularCovers + price/colour). Fall back to catalog
  // attribute order when `c_allVariantsData` / variants are unavailable (slim hits).
  let coverValues = coverAttr.values;
  const rawVariants = (() => {
    const raw = product?.c_allVariantsData;

    if (!raw) return product?.variants ?? [];

    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;

      return Array.isArray(parsed) && parsed.length > 0 ? parsed : (product?.variants ?? []);
    } catch {
      return product?.variants ?? [];
    }
  })();

  if (Array.isArray(rawVariants) && rawVariants.length > 0) {
    const orderedVariants = sortCoverVariantsForDisplay(
      rawVariants,
      getPopularCoverIds(product)
    );

    coverValues = orderCoverAttributeValues(orderedVariants, coverAttr.values);
  }

  let coverVariants;
  const variantForCover = (coverValue) => {
    if (coverVariants === undefined) {
      coverVariants = buildCoverVariantIndex(product);
    }

    return coverVariants?.get(String(coverValue));
  };

  const stopAt = Number.isFinite(limit) && limit >= 0 ? limit + 1 : Infinity;
  const out = [];

  for (const value of coverValues) {
    const swatchUrl = variantForCover(value.value)?.pimlyProductImageUrl || null;

    if (!swatchUrl) continue;

    out.push({...value, swatchUrl});

    if (out.length >= stopAt) break;
  }

  return out;
}

/**
 * How many covers the product is offered in — independent of how many resolve to a displayable
 * swatch image, so assistive copy can stay factual while the chip row only shows what it can render.
 *
 * @param {object|null|undefined} product
 * @returns {number}
 */
export function getCoverCount(product) {
  const coverAttr = product?.variationAttributes?.find(
    ({id}) => id === COVER_SWATCH_ATTRIBUTE_ID
  );

  return coverAttr?.values?.length ?? 0;
}

/**
 * One cover line for compact UIs (e.g. Shop the look drawer): prefers the first orderable
 * variant's `cover`, then the first orderable cover value with an image, then any with an image,
 * else the first cover value.
 *
 * @param {object|null|undefined} product
 * @returns {{label: string, swatchUrl: string|undefined}|null}
 */
export function getPrimaryCoverSwatchDisplay(product) {
  const coverAttr = product?.variationAttributes?.find(({id}) => id === COVER_SWATCH_ATTRIBUTE_ID);

  if (!coverAttr?.values?.length) return null;

  const {values} = coverAttr;
  const attrId = coverAttr.id;
  let picked = null;

  const variants = product?.variants;

  if (Array.isArray(variants) && variants.length > 0) {
    const v = variants.find((x) => x?.orderable !== false) ?? variants[0];
    const cv = v?.variationValues?.cover;

    if (cv != null && cv !== '') {
      picked = values.find((x) => x.value === cv) ?? null;
    }
  }

  if (!picked) {
    picked =
      values.find((x) => x.orderable !== false && resolveSwatchImage(product, {[attrId]: x.value})) ??
      values.find((x) => resolveSwatchImage(product, {[attrId]: x.value})) ??
      values[0];
  }

  if (!picked) return null;

  const img = resolveSwatchImage(product, {[attrId]: picked.value});
  const swatchUrl = img?.disBaseLink || img?.link;
  const label = (picked.name != null && String(picked.name).trim() !== '' ? picked.name : null) ??
    (picked.value != null ? String(picked.value) : '');

  if (!label && !swatchUrl) return null;

  return {label, swatchUrl: swatchUrl || undefined};
}
